import React, { useContext, useEffect, useState } from "react";
import { apiGet, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import { AiFillCheckCircle, AiFillCloseCircle } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { BsArrowUpShort } from "react-icons/bs";
import AuthContext from "context/AuthContext";
import { isEmpty } from "lodash";
import helper from "../../utils/helpers";
import OButton from "components/reusable/OButton";
import OInputField from "components/reusable/OInputField";
import { useForm } from "react-hook-form";

const OrderDiscountsList = ({
  page,
  sort,
  setSort,
  list,
  fetchOrderDiscounts,
  manager,
  paginationObj,
}) => {
  const { t } = useTranslation();
  const { user, config } = useContext(AuthContext);
  const notification = useToastContext();
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });

  const getStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      accepted: { color: "bg-green-100 text-green-800", text: "Accepted" },
      cancelled: { color: "bg-red-100 text-red-800", text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const getDiscountTypeBadge = (requestType) => {
    if (!requestType) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    const typeConfig = {
      percentage: { color: "bg-blue-100 text-blue-800", text: "Percentage" },
      amount: { color: "bg-purple-100 text-purple-800", text: "Fixed Amount" },
    };

    const config = typeConfig[requestType] || typeConfig.amount;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const formatDiscountValue = (requestType, discountValue) => {
    if (!requestType || !discountValue) {
      return "N/A";
    }
    if (requestType === "percentage") {
      return `${discountValue}%`;
    } else {
      return `${helper.priceFormat(discountValue)} ${config?.currency}`;
    }
  };

  const openActionModal = (discount, action) => {
    setSelectedDiscount(discount);
    setActionType(action);
    setShowActionModal(true);
    reset({
      adminComment: "",
      approvedRequestValue:
        action === "approve" ? discount?.discountValue : undefined,
    });
  };

  const handleAction = async (data) => {
    if (!data.adminComment) {
      notification.error("Admin comment is required");
      return;
    }

    try {
      setActionLoading(true);
      const payload = {
        status: actionType === "approve" ? "accepted" : "cancelled",
        adminComment: data.adminComment,
        ...(actionType === "approve" && {
          approvedRequestValue: data?.approvedRequestValue,
        }),
      };
      const path = `${apiPath.orderDiscounts}/${selectedDiscount?._id}/status`;
      const result = await apiPut(path, payload);
      if (result?.data?.success) {
        notification.success(result.data.message);
        setShowActionModal(false);
        setSelectedDiscount(null);
        setActionType("");
        fetchOrderDiscounts();
      } else {
        notification.error(
          result?.data?.message || t("O_SOMETHING_WENT_WRONG")
        );
      }
    } catch (e) {
      notification.error(e?.message || t("O_SOMETHING_WENT_WRONG"));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="p-3">
        <div
          className="overflow-x-auto relative 
 border max-h-none md:max-h-[70vh] overflow-y-auto"
        >
          <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400">
            <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400 sticky top-0 z-0">
              <tr>
                <th scope="col" className="py-2 px-2 md:px-3">
                  #
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 cursor-pointer"
                  onClick={() => {
                    if (
                      sort.sortKey === "projectTitle" &&
                      sort.sortType === "asc"
                    ) {
                      setSort({
                        sortKey: "projectTitle",
                        sortType: "desc",
                      });
                    } else {
                      setSort({
                        sortKey: "projectTitle",
                        sortType: "asc",
                      });
                    }
                  }}
                >
                  <div className="flex">
                    <span>Project</span>
                    <span>
                      {sort.sortKey === "projectTitle" &&
                        sort.sortType === "asc" && (
                          <BsArrowUpShort className="w-4 h-4" />
                        )}
                      {sort.sortKey === "projectTitle" &&
                        sort.sortType === "desc" && (
                          <BsArrowUpShort className="w-4 h-4 rotate-180" />
                        )}
                    </span>
                  </div>
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 cursor-pointer hidden md:table-cell"
                  onClick={() => {
                    if (
                      sort.sortKey === "agentName" &&
                      sort.sortType === "asc"
                    ) {
                      setSort({
                        sortKey: "agentName",
                        sortType: "desc",
                      });
                    } else {
                      setSort({
                        sortKey: "agentName",
                        sortType: "asc",
                      });
                    }
                  }}
                >
                  <div className="flex">
                    <span>Agent</span>
                    <span>
                      {sort.sortKey === "agentName" &&
                        sort.sortType === "asc" && (
                          <BsArrowUpShort className="w-4 h-4" />
                        )}
                      {sort.sortKey === "agentName" &&
                        sort.sortType === "desc" && (
                          <BsArrowUpShort className="w-4 h-4 rotate-180" />
                        )}
                    </span>
                  </div>
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 hidden md:table-cell"
                >
                  Unit Number
                </th>
                <th scope="col" className="py-2 px-2 md:px-3">
                  Request For
                </th>
                <th scope="col" className="py-2 px-2 md:px-3">
                  Discount Type
                </th>
                <th scope="col" className="py-2 px-2 md:px-3">
                  Discount Value
                </th>
                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 hidden lg:table-cell"
                >
                  Agent Comment
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 cursor-pointer"
                  onClick={() => {
                    if (sort.sortKey === "status" && sort.sortType === "asc") {
                      setSort({
                        sortKey: "status",
                        sortType: "desc",
                      });
                    } else {
                      setSort({
                        sortKey: "status",
                        sortType: "asc",
                      });
                    }
                  }}
                >
                  <div className="flex">
                    <span>Status</span>
                    <span>
                      {sort.sortKey === "status" && sort.sortType === "asc" && (
                        <BsArrowUpShort className="w-4 h-4" />
                      )}
                      {sort.sortKey === "status" &&
                        sort.sortType === "desc" && (
                          <BsArrowUpShort className="w-4 h-4 rotate-180" />
                        )}
                    </span>
                  </div>
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 hidden lg:table-cell"
                >
                  Admin Comment
                </th>

                <th
                  scope="col"
                  className="py-2 px-2 md:px-3 cursor-pointer"
                  onClick={() => {
                    if (
                      sort.sortKey === "createdAt" &&
                      sort.sortType === "asc"
                    ) {
                      setSort({
                        sortKey: "createdAt",
                        sortType: "desc",
                      });
                    } else {
                      setSort({
                        sortKey: "createdAt",
                        sortType: "asc",
                      });
                    }
                  }}
                >
                  <div className="flex">
                    <span>Created At</span>
                    <span>
                      {sort.sortKey === "createdAt" &&
                        sort.sortType === "asc" && (
                          <BsArrowUpShort className="w-4 h-4" />
                        )}
                      {sort.sortKey === "createdAt" &&
                        sort.sortType === "desc" && (
                          <BsArrowUpShort className="w-4 h-4 rotate-180" />
                        )}
                    </span>
                  </div>
                </th>

                <th scope="col" className="py-2 px-2 md:px-3 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {list &&
                Array.isArray(list) &&
                list?.map((item, i) => (
                  <tr
                    key={i}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <th
                      scope="row"
                      className="py-2 px-2 md:px-3 border-r font-medium text-gray-900 dark:text-white"
                    >
                      {i + 1 + 10 * (paginationObj?.page - 1)}
                    </th>
                    <td className="py-2 px-2 md:px-3 border-r text-DarkBlue">
                      {item?.projectTitle || "N/A"}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r hidden md:table-cell">
                      {item?.agentName || "N/A"}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r hidden md:table-cell">
                      {item?.unitNumber || "N/A"}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {item?.requestFor || "N/A"}
                      </span>
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r">
                      {getDiscountTypeBadge(item?.requestType)}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r">
                      {formatDiscountValue(
                        item?.requestType,
                        item?.discountValue
                      )}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r hidden lg:table-cell">
                      <div
                        className="max-w-[120px] truncate"
                        title={item?.agentComment || "N/A"}
                      >
                        {item?.agentComment || "N/A"}
                      </div>
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r">
                      {getStatusBadge(item?.status)}
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r hidden lg:table-cell">
                      <div
                        className="max-w-[120px] truncate"
                        title={
                          item?.adminComment ||
                          (item?.status === "pending" ? "-" : "N/A")
                        }
                      >
                        {item?.adminComment ||
                          (item?.status === "pending" ? "-" : "N/A")}
                      </div>
                    </td>
                    <td className="py-2 px-2 md:px-3 border-r">
                      {helper.dateFormat(item?.createdAt) || "N/A"}
                    </td>
                    <td className="py-1 px-2 md:px-2 border-l">
                      <div className="">
                        <ul className="flex justify-center">
                          {item?.status === "pending" && (
                            <>
                              <li className="px-1 py-1 hover:bg-white hover:text-LightBlue">
                                <button
                                  title="Approve"
                                  onClick={() =>
                                    openActionModal(item, "approve")
                                  }
                                >
                                  <AiFillCheckCircle className="cursor-pointer w-5 h-5 text-green-600" />
                                </button>
                              </li>
                              <li className="px-1 py-1 hover:bg-white hover:text-LightBlue">
                                <button
                                  title="Decline"
                                  onClick={() =>
                                    openActionModal(item, "decline")
                                  }
                                >
                                  <AiFillCloseCircle className="cursor-pointer w-5 h-5 text-red-600" />
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              {(!list || !Array.isArray(list) || isEmpty(list)) && (
                <tr className="bg-white border-b w-full text-center dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4 px-6" colSpan={10}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="modal">
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative my-6 w-full max-w-[600px] mx-auto">
              <div className="sm:py-4 sm:px-2 py-8 px-7">
                <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                  <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                    <h3 className="text-xl font-semibold">
                      {actionType === "approve" ? "Approve" : "Decline"}{" "}
                      Discount Request
                    </h3>
                    <button
                      className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                      onClick={() => {
                        setShowActionModal(false);
                        setSelectedDiscount(null);
                        setActionType("");
                        reset();
                      }}
                    >
                      <span className="text-[#B8BBBF] text-4xl" title="Close">
                        ×
                      </span>
                    </button>
                  </div>

                  <div className="p-5">
                    {/* Request Details */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-lg font-medium text-gray-900 mb-3">
                        Request Details
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Project:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.projectTitle}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Unit:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitNumber}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Agent:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.agentName}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Request For:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.requestFor}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Discount Type:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {getDiscountTypeBadge(
                              selectedDiscount?.requestType
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Discount Value:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {formatDiscountValue(
                              selectedDiscount?.requestType,
                              selectedDiscount?.discountValue
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Unit Total Area:
                          </span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitTotalArea ?? "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Unit Price:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitPrice != null
                              ? `${helper.priceFormat(
                                  selectedDiscount.unitPrice
                                )} ${config?.currency}`
                              : "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Unit Commission:
                          </span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitCommission != null
                              ? `${helper.priceFormat(
                                  selectedDiscount.unitCommission
                                )} ${config?.currency}`
                              : "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Unit Commission Tax:
                          </span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitCommissionTax != null
                              ? `${helper.priceFormat(
                                  selectedDiscount.unitCommissionTax
                                )} ${config?.currency}`
                              : "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Unit Built Area:
                          </span>
                          <span className="ml-2 font-medium text-gray-900">
                            {selectedDiscount?.unitBuiltArea ?? "N/A"}
                          </span>
                        </div>
                      </div>
                      {selectedDiscount?.agentComment && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-gray-500 text-sm">
                            Agent Comment:
                          </span>
                          <p className="mt-1 text-sm text-gray-700 bg-white p-2 rounded border">
                            {selectedDiscount.agentComment}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Admin Comment Form */}
                    <form onSubmit={handleSubmit(handleAction)}>
                      <div className="mb-6">
                        <label
                          htmlFor="adminComment"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Admin Comment <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="adminComment"
                          name="adminComment"
                          rows={4}
                          className="block w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          placeholder={`Enter your comment for ${
                            actionType === "approve" ? "approving" : "declining"
                          } this discount request...`}
                          {...register("adminComment", {
                            required: "Admin comment is required",
                            minLength: {
                              value: 10,
                              message: "Comment must be at least 10 characters",
                            },
                          })}
                        />
                        {errors.adminComment && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.adminComment.message}
                          </p>
                        )}
                      </div>

                      {actionType === "approve" && (
                        <div className="mb-6">
                          <label
                            htmlFor="approvedRequestValue"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Approved{" "}
                            {selectedDiscount?.requestType === "percentage"
                              ? "Percentage"
                              : "Amount"}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="approvedRequestValue"
                            name="approvedRequestValue"
                            type="number"
                            step={
                              selectedDiscount?.requestType === "percentage"
                                ? "0.01"
                                : "0.01"
                            }
                            min={0}
                            max={
                              selectedDiscount?.requestType === "percentage"
                                ? 100
                                : undefined
                            }
                            className="block w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={
                              selectedDiscount?.requestType === "percentage"
                                ? "Enter approved percentage (0 - 100)"
                                : `Enter approved amount in ${config?.currency}`
                            }
                            {...register("approvedRequestValue", {
                              required: "Approved value is required",
                              min: {
                                value: 0,
                                message:
                                  "Value must be greater than or equal to 0",
                              },
                              ...(selectedDiscount?.requestType === "percentage"
                                ? {
                                    max: {
                                      value: 100,
                                      message: "Percentage cannot exceed 100",
                                    },
                                  }
                                : {}),
                              setValueAs: (v) =>
                                v === "" || v === null || v === undefined
                                  ? undefined
                                  : Number(v),
                            })}
                          />
                          {errors.approvedRequestValue && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.approvedRequestValue.message}
                            </p>
                          )}
                          {selectedDiscount?.requestType !== "percentage" && (
                            <p className="mt-1 text-xs text-gray-500">
                              Currency: {config?.currency}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowActionModal(false);
                            setSelectedDiscount(null);
                            setActionType("");
                            reset();
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={actionLoading}
                          className={`px-6 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            actionType === "approve"
                              ? "bg-green-600 hover:bg-green-700 disabled:bg-green-400"
                              : "bg-red-600 hover:bg-red-700 disabled:bg-red-400"
                          }`}
                        >
                          {actionLoading
                            ? "Processing..."
                            : actionType === "approve"
                            ? "Approve Request"
                            : "Decline Request"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black" />
        </div>
      )}
    </>
  );
};

export default OrderDiscountsList;
